import React, { useEffect, useState, useCallback } from 'react';
import styles from './index.module.scss';
import { getImageFromS3 } from '../../../imageService';
import sgmService from '../../../services/sgmService';
import QrLimitComponent from '../qr-limit-component';
import moment from 'moment';

const QR_STORAGE_KEY = 'sgm_qr_code';
// SGM mirrors the MGM error codes in the 3000xx family (MGM 2000xx).
// 300001 - ліміт генерацій, 300003 - денний ліміт успішних реєстрацій.
const QR_LIMIT_ERROR_CODES = [300001, 300003];

const getStoredQrCode = () => {
  const stored = localStorage.getItem(QR_STORAGE_KEY);
  if (!stored) return null;

  try {
    return JSON.parse(stored);
  } catch (error) {
    console.error('Помилка читання QR-коду з localStorage:', error);
    return null;
  }
};

const saveQrCode = (qrCode) => {
  try {
    localStorage.setItem(QR_STORAGE_KEY, JSON.stringify(qrCode));
  } catch (error) {
    console.error('Помилка збереження QR-коду в localStorage:', error);
  }
};

const isQrCodeValid = (qrCode) => {
  if (!qrCode?.expires_at) return false;
  return moment(qrCode.expires_at).isAfter(moment());
};

const QrCodeComponent = ({ setScreenNumber, onQuestionClick }) => {
  const [balance, setBalance] = useState(0);
  const [qrData, setQrData] = useState(null);
  const [timeLeft, setTimeLeft] = useState('00:00');
  const [copied, setCopied] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [isExpired, setIsExpired] = useState(false);

  const fetchQrCode = useCallback(async () => {
    try {
      const qrCode = await sgmService.generateQrCode();
      setQrData(qrCode);
      if (qrCode) {
        saveQrCode(qrCode);
      }
    } catch (error) {
      if (QR_LIMIT_ERROR_CODES.includes(Number(error?.response?.data?.code))) {
        const storedQrCode = getStoredQrCode();
        if (storedQrCode) {
          setQrData(storedQrCode);
        }
        setShowLimitModal(true);
      } else {
        console.error('Помилка генерації QR-коду:', error);
      }
    }
  }, []);

  useEffect(() => {
    (async () => {
      const balanceData = await sgmService.getBalance();
      setBalance(balanceData?.balance || 0);

      const storedQrCode = getStoredQrCode();
      if (isQrCodeValid(storedQrCode)) {
        setQrData(storedQrCode);
      } else {
        await fetchQrCode();
      }
    })();
  }, [fetchQrCode]);

  useEffect(() => {
    if (!qrData?.expires_at) return;

    const updateTimer = () => {
      const now = moment();
      const expires = moment(qrData.expires_at);
      const diff = expires.diff(now);

      if (diff <= 0) {
        setTimeLeft('00:00');
        setIsExpired(true);
      } else {
        setIsExpired(false);
        const duration = moment.duration(diff);
        const minutes = Math.floor(duration.asMinutes());
        const seconds = duration.seconds();
        setTimeLeft(
          `${minutes.toString().padStart(2, '0')}:${seconds
            .toString()
            .padStart(2, '0')}`
        );
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [qrData]);

  const handleCopyLink = () => {
    const link = qrData?.qr_short_url || qrData?.qr_link;

    if (link) {
      navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className={styles.container}>
      <img
        src={getImageFromS3('/images/mgm/bg.png')}
        alt="background"
        className={styles.backgroundImage}
      />
      <img
        className={styles.glassHeadBg}
        src={getImageFromS3('/images/mgm/big-glass-head-bg.png')}
        alt="glass-head"
      />
      <div className={styles.content}>
        <div className={styles.header}>
          <svg
            width="20"
            height="18"
            viewBox="0 0 20 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            onClick={() => setScreenNumber(1)}
          >
            <path
              d="M4.29675 8.59375L9.66769 13.9647C10.261 14.5579 10.261 15.5198 9.66769 16.1131C9.07444 16.7063 8.11258 16.7063 7.51932 16.1131L0.707108 9.30086C0.316583 8.91033 0.316583 8.27717 0.707107 7.88664L7.51932 1.07443C8.11258 0.481175 9.07444 0.481175 9.66769 1.07443C10.261 1.66769 10.261 2.62955 9.66769 3.22281L4.29675 8.59375Z"
              fill="white"
            />
            <rect
              x="2.78076"
              y="6.99902"
              width="17"
              height="3"
              rx="1.5"
              fill="white"
            />
          </svg>
          <div onClick={onQuestionClick}>
            Є питання?
            <svg
              width="27"
              height="21"
              viewBox="0 0 27 21"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1.86207 0C0.837931 0 0 0.821739 0 1.82609V16.4348L2.7931 13.6957H20.4828C21.5069 13.6957 22.3448 12.8739 22.3448 11.8696V1.82609C22.3448 0.821739 21.5069 0 20.4828 0H1.86207ZM23.2759 7.30435V11.8696C23.2759 13.4217 22.0655 14.6087 20.4828 14.6087H13.0345V16.4348C13.0345 17.4391 13.8724 18.2609 14.8966 18.2609H24.2069L27 21V9.13043C27 8.12609 26.1621 7.30435 25.1379 7.30435H23.2759Z"
                fill="white"
              />
            </svg>
          </div>
        </div>
        <div className={styles.pointBalanceContainer}>
          <p>
            Баланс балів <b>{balance}</b>
          </p>
        </div>
        <h1>Ваш код згенеровано</h1>
        <div className={styles.qrCode}>
          {qrData?.qr_link && (
            <img
              className={styles.qrImage}
              src={qrData.qr_link}
              alt="QR-code"
            />
          )}
        </div>
        <p className={styles.statusText}>
          {isExpired ? '❌ Неактивний' : '✅ Активний'} {timeLeft}
        </p>
        <button className={styles.updateButton} onClick={fetchQrCode}>
          Оновити QR-code
        </button>
        <p
          className={styles.copyLink}
          onClick={handleCopyLink}
          style={{ cursor: 'pointer' }}
        >
          Скопіювати лінк
          <svg
            width="11"
            height="13"
            viewBox="0 0 11 13"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect width="8.125" height="10.5625" rx="2" fill="white" />
            <rect
              x="2.9375"
              y="2.9375"
              width="7.125"
              height="9.5625"
              rx="1.5"
              stroke="white"
            />
          </svg>
        </p>
        {copied && (
          <p className={styles.copiedToast}>Лінк на QR-code скопійовано!</p>
        )}
        <p className={styles.text} style={{ marginTop: copied ? 32 : 72 }}>
          <b>Як працює механіка?</b>
        </p>
        <p className={styles.text} style={{ marginTop: 14 }}>
          Покажіть QR‑код або поділіться персональним{' '}
          <b>посиланням із повнолітнім курцем</b>, який ще не брав участі в
          програмі. Після того як залучений користувач зареєструється та пройде
          опитувальник, вам будуть нараховані бали.
        </p>
        <p className={styles.text} style={{ marginTop: 8 }}>
          <b>
            Зверніть увагу: пропозиція доступна лише для повнолітніх курців.
          </b>
        </p>
        <p className={styles.text} style={{ marginTop: 8 }}>
          Один код можна використати лише для однієї реєстрації. Для кожного
          нового користувача необхідно згенерувати окремий код.
        </p>
        <p className={styles.text} style={{ marginTop: 8 }}>
          Код дійсний протягом однієї години, після чого його потрібно оновити.
        </p>
        <div className={styles.footer}>
          <button onClick={() => setScreenNumber(2)}>Правила програм</button>
          <button
            onClick={() =>
              window.open(
                'https://www.pmiprivacy.com/ukraine/uk/consumer/',
                '_blank'
              )
            }
          >
            Політика конфіденційності
          </button>
          <button onClick={() => setScreenNumber(10)}>FAQ</button>
          <button onClick={() => setScreenNumber(11)}>
            Параметри застосування файлів cookie
          </button>
        </div>
      </div>
      {showLimitModal && (
        <QrLimitComponent onMainPageClick={() => setScreenNumber(1)} />
      )}
    </div>
  );
};

export default QrCodeComponent;
